import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import LaunchSpecialTimer from '../components/LaunchSpecialTimer';
import { APP_STORE_URL, PLAY_STORE_URL, getDownloadUrl } from '../storeLinks';

function Pricing() {
  const { t } = useTranslation();
  const downloadUrl = getDownloadUrl();
  const navLinks = [
    { label: t('nav.home'), to: '/' },
    { label: t('nav.journal'), to: '/blog' },
    { label: t('nav.download'), to: downloadUrl, external: true, cta: true },
  ];

  const paidTiers = [
    {
      key: 'pro',
      featured: true,
      ctaClass: 'tier-btn-primary',
    },
    {
      key: 'business',
      featured: false,
      ctaClass: 'tier-btn-secondary',
    },
  ];

  return (
    <div className="pricing-page">
      <Navbar links={navLinks} />

      <section className="pricing-hero">
        <div className="container">
          <span className="launch-special-banner">{t('pricing.launchLabel')}</span>
          <h1>{t('pricing.heroHeading')}</h1>
          <p className="pricing-subtitle">
            {t('pricing.heroSubtitle')}
          </p>
          <LaunchSpecialTimer />
          <div className="launch-special-lockin">
            {t('pricing.lockin')}
          </div>
          <div className="launch-special-origin">
            {t('pricing.origin')}
          </div>
        </div>
      </section>

      <section className="pricing-tiers">
        <div className="container">
          <div className="pricing-section-copy">
            <span className="pricing-eyebrow">{t('pricing.tiersEyebrow')}</span>
            <h2>{t('pricing.tiersHeading')}</h2>
            <p>
              {t('pricing.tiersBody')}
            </p>
          </div>

          <div className="tiers-grid">
            <div className="tier">
              <div className="tier-header">
                <h3>{t('pricing.starter.name')}</h3>
                <div className="tier-price">
                  <span className="price">{t('pricing.starter.priceFree')}</span>
                </div>
                <p className="tier-desc">{t('pricing.starter.desc')}</p>
              </div>
              <ul className="tier-features">
                {t('pricing.starter.features', { returnObjects: true }).map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
              <a href={downloadUrl} className="tier-btn tier-btn-secondary" target="_blank" rel="noopener noreferrer">
                {t('pricing.starter.cta')}
              </a>
            </div>

            {paidTiers.map((tier) => {
              const specialPrice = t(`pricing.${tier.key}.specialPrice`);
              return (
                <div key={tier.key} className={`tier ${tier.featured ? 'tier-featured' : ''}`}>
                  <div className="tier-badge">{t(`pricing.${tier.key}.badge`)}</div>
                  <div className="tier-header">
                    <h3>{t(`pricing.${tier.key}.name`)}</h3>
                    <div className="tier-price tier-price-special">
                      <span className="price-original">{t(`pricing.${tier.key}.regularPrice`)}</span>
                      <span className="currency">$</span>
                      <span className="price">{specialPrice.replace(/[^0-9]/g, '')}</span>
                      <span className="period">{t('pricing.perMonth')}</span>
                    </div>
                    <p className="tier-desc">{t(`pricing.${tier.key}.desc`)}</p>
                    <p className="tier-special-note">{t('pricing.specialNote')}</p>
                  </div>
                  <ul className="tier-features">
                    {t(`pricing.${tier.key}.features`, { returnObjects: true }).map((feature) => (
                      <li key={feature}>{feature}</li>
                    ))}
                  </ul>
                  <a href={downloadUrl} className={`tier-btn ${tier.ctaClass}`} target="_blank" rel="noopener noreferrer">
                    {t(`pricing.${tier.key}.cta`)}
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="pricing-value">
        <div className="container">
          <h2>{t('pricing.value.heading')}</h2>
          <div className="value-grid">
            <div className="value-item">
              <div className="value-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </div>
              <h4>{t('pricing.value.item1Title')}</h4>
              <p>
                {t('pricing.value.item1Body')}
              </p>
            </div>
            <div className="value-item">
              <div className="value-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
              <h4>{t('pricing.value.item2Title')}</h4>
              <p>
                {t('pricing.value.item2Body')}
              </p>
            </div>
            <div className="value-item">
              <div className="value-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12,6 12,12 16,14"/>
                </svg>
              </div>
              <h4>{t('pricing.value.item3Title')}</h4>
              <p>
                {t('pricing.value.item3Body')}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="pricing-compare">
        <div className="container">
          <h2>{t('pricing.compare.heading')}</h2>
          <p className="compare-subtitle">
            {t('pricing.compare.subtitle')}
          </p>
          <div className="compare-grid">
            <div className="compare-item">
              <span className="compare-price">{t('pricing.compare.legalLabel')}</span>
              <span className="compare-label">{t('pricing.compare.legalSub')}</span>
              <span className="compare-note">{t('pricing.compare.legalNote')}</span>
            </div>
            <div className="compare-item">
              <span className="compare-price">{t('pricing.compare.financeLabel')}</span>
              <span className="compare-label">{t('pricing.compare.financeSub')}</span>
              <span className="compare-note">{t('pricing.compare.financeNote')}</span>
            </div>
            <div className="compare-item">
              <span className="compare-price">{t('pricing.compare.execLabel')}</span>
              <span className="compare-label">{t('pricing.compare.execSub')}</span>
              <span className="compare-note">{t('pricing.compare.execNote')}</span>
            </div>
            <div className="compare-item">
              <span className="compare-price">{t('pricing.compare.opsLabel')}</span>
              <span className="compare-label">{t('pricing.compare.opsSub')}</span>
              <span className="compare-note">{t('pricing.compare.opsNote')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Ubuntu Program */}
      <section className="pricing-ubuntu">
        <div className="container">
          <div className="ubuntu-card">
            <div className="ubuntu-card-badge">{t('pricing.ubuntu.badge')}</div>
            <h2>{t('pricing.ubuntu.heading')}</h2>
            <p className="ubuntu-card-desc">
              {t('pricing.ubuntu.desc')}
            </p>
            <div className="ubuntu-card-tracks">
              <div className="ubuntu-card-track">
                <h4>{t('pricing.ubuntu.track1Title')}</h4>
                <p>{t('pricing.ubuntu.track1Body')}</p>
              </div>
              <div className="ubuntu-card-track">
                <h4>{t('pricing.ubuntu.track2Title')}</h4>
                <p>{t('pricing.ubuntu.track2Body')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pricing-cta">
        <div className="container">
          <h2>{t('pricing.cta.heading')}</h2>
          <p>{t('pricing.cta.body')}</p>
          <div className="cta-store-btns">
            <a href={APP_STORE_URL} className="cta-btn" target="_blank" rel="noopener noreferrer">
              {t('pricing.cta.appStore')}
            </a>
            <a href={PLAY_STORE_URL} className="cta-btn cta-btn-secondary" target="_blank" rel="noopener noreferrer">
              {t('pricing.cta.googlePlay')}
            </a>
          </div>
        </div>
      </section>

      <footer>
        <div className="container">
          <div className="footer-brand">
            <img src="/logo192.png" alt={t('landing.hero.logoAlt')} className="footer-logo" />
            <span>10 Thirty</span>
          </div>
          <p>
            {t('landing.footer.copyright', { year: new Date().getFullYear() })}{' '}
            {t('landing.footer.broughtToYou')}{' '}
            <a href="https://www.callitservices.co.za" target="_blank" rel="noopener noreferrer">{t('landing.footer.callItServices')}</a>
          </p>
          <div className="links">
            <Link to="/blog">{t('landing.footer.linkJournal')}</Link>
            <Link to="/">{t('landing.footer.linkHome')}</Link>
            <Link to="/pitch">{t('landing.footer.linkInvestors')}</Link>
            <Link to="/privacy">{t('landing.footer.linkPrivacy')}</Link>
            <Link to="/terms">{t('landing.footer.linkTerms')}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Pricing;
